import React, { useMemo } from "react";
import Tooltip from "../../components/ui/tooltip/Tooltip";
import dayjs from "dayjs";
import clsx from "clsx";
import { getDocumentsStatusIcon } from "../../utils/helpers/statusIcons";
import { IDocument } from "../../types/documents";
import { useTranslation } from "react-i18next";
import { ReactComponent as AlertIcon } from "../../assets/images/icons/alert.svg";
import { ReactComponent as UploadIcon } from "../../assets/images/icons/upload.svg";
import { ReactComponent as DownloadIcon } from "../../assets/images/icons/download.svg";
import { ReactComponent as NotesIcon } from "../../assets/images/icons/notes.svg";

interface Props {
  document: IDocument;
  id: number;
  hasRedBg: boolean;
  downloadTemplate: (id: string) => Promise<void>;
  openCloseSidebarDocuments: (document: IDocument | null) => void;
  openCloseModalPdfDocuments: (document: IDocument | null) => void;
  openCloseSubDocumentsModal: (document: IDocument) => void;
  hasTemplate?: boolean;
}

const DocumentCard = (props: Props) => {
  const {
    document,
    id,
    hasRedBg,
    downloadTemplate,
    openCloseSidebarDocuments,
    openCloseModalPdfDocuments,
    openCloseSubDocumentsModal,
    hasTemplate
  } = props;

  const { t } = useTranslation();

  const hasSubDocuments = useMemo(() => document.subDocuments?.length > 0, [document]);

  return (
    <div
      key={id}
      className={
        "col-span-12 md:col-span-6 lg:col-span-4 xl:col-span-3 md:flex md:flex-col md:justify-between rounded-[8px] shadow-card select-none p-4" +
        (hasRedBg ? " bg-[#FFEAED]" : " bg-lightGrey")
      }
    >
      <div>
        {/* Upload Requested */}

        <>
          {document.action === 3 && (
            <div className="bg-white font-bold text-red text-[13px] uppercase leading-[16px] flex justify-center p-[10px] rounded-[4px] mb-2">
              <div className="mr-1 grow">
                <div className="text-center">
                  {t("screens.documents.uploadRequested")}
                </div>
              </div>

              <div className="relative">
                <div
                  className="cursor-pointer"
                  id={`tooltip-documents-uploadRequested-trigger-${document.id || document.subDocuments?.[0]?.id}`}
                >
                  <AlertIcon />
                </div>


                <Tooltip
                  id={`tooltip-documents-uploadRequested-${document.id || document.subDocuments?.[0]?.id}`}
                  triggerId={`tooltip-documents-uploadRequested-trigger-${document.id || document.subDocuments?.[0]?.id}`}
                  title={t("screens.documents.uploadRequested")}
                  description={document.allowsMultiple ? t("screens.documents.uploadMultipleRequestedText") : t("screens.documents.uploadRequestedText")}
                />
              </div>
            </div>

          )}



          {(document.action !== 3 && document.action !== 4 && document.action !== 0 && document.statusCode != 605530002) && (
            <div className="bg-white font-bold text-red text-[13px] uppercase leading-[16px] flex justify-center p-[10px] rounded-[4px] mb-2">
              <div className="mr-1 grow">
                <div className="text-center">
                  {t("screens.documents.uploadNotMandatory")}
                </div>
              </div>
            </div>
          )}
        </>

        {/* Acceptance Request */}
        {document.mustBeAccepted && (
          <div className="bg-white font-bold text-red text-[13px] uppercase leading-[16px] flex justify-center p-[10px] rounded-[4px] mb-2">
            <div className="mr-1 grow">
              <div className="text-center">
                {t("screens.documents.acceptanceRequest")}
              </div>
            </div>
          </div>
        )}

        {/* Update Request */}
        {document.isExpiring === true && (
          <div className="bg-white font-bold text-red text-[13px] uppercase leading-[16px] flex justify-center p-[10px] rounded-[4px] mb-2">
            <div className="mr-1 grow">
              <div className="text-center">
                {t("screens.documents.updateRequested")}
              </div>
            </div>

            <div className="relative">
              <div
                className="cursor-pointer"
                id={`tooltip-documents-updateRequest-trigger-${document.id || document.subDocuments?.[0]?.id}`}
              >
                <AlertIcon />
              </div>

              <Tooltip
                id={`tooltip-documents-updateRequest-${document.id || document.subDocuments?.[0]?.id}`}
                triggerId={`tooltip-documents-updateRequest-trigger-${document.id || document.subDocuments?.[0]?.id}`}
                title={t("screens.documents.updateRequested")}
                description={t("screens.documents.updateRequestedText", {
                  days_before_blocking: document.daysBeforeBlocking,
                })}
              />
            </div>
          </div>
        )}

        {/* Upload template */}
        {document.action === 4 && (
          <div className="bg-white font-bold text-red text-[13px] uppercase leading-[16px] flex justify-center p-[10px] rounded-[4px] mb-2">
            <div className="mr-1 grow">
              <div className="text-center">
                {t("screens.documents.uploadTemplate")}
              </div>
            </div>

            <div className="relative">
              <div
                className="cursor-pointer"
                id={`tooltip-documents-uploadTemplate-trigger-${document.id || document.subDocuments?.[0]?.id}`}
              >
                <AlertIcon />
              </div>

              <Tooltip
                id={`tooltip-documents-uploadTemplate-${document.id || document.subDocuments?.[0]?.id}`}
                triggerId={`tooltip-documents-uploadTemplate-trigger-${document.id || document.subDocuments?.[0]?.id}`}
                title={t("screens.documents.uploadTemplate")}
                description={t("screens.documents.uploadTemplateText")}
              />
            </div>
          </div>
        )}

        <div className="mb-9">
          <div
            onClick={() => {
              if (hasSubDocuments) {
                openCloseSubDocumentsModal(document);
              } else if (document.uploadedDocument) {
                openCloseModalPdfDocuments(document);
              }
            }}
            className={clsx(
              "uppercase text-black font-bold text-[18px] mb-1 text-ellipsis-2",
              {
                "cursor-pointer underline":
                  !!(document.action === 4),
              }
            )}
          >
            {document.name}
          </div>
          {!hasSubDocuments && <div>{document.documentSubType}</div>}
          {hasSubDocuments && (
            <div
              className="cursor-pointer underline"
              onClick={() => openCloseSubDocumentsModal(document)}
            >
              {t("screens.documents.nDocuments", {
                value: document.subDocuments?.length || 0,
              })}
            </div>
          )}
        </div>
      </div>
      <div>
        <div className="flex justify-between mb-4 ">
          {document.enableUpload && (
            <div
              onClick={() => openCloseSidebarDocuments(document)}
              className="h-[36px] w-[36px] bg-red rounded-[8px] flex items-center justify-center cursor-pointer"
            >
              <UploadIcon />
            </div>
          )}

          {document.action === 4 && (
            <div className="cursor-pointer flex h-full items-center text-red underline text-sm font-semibold">
              <div>
                <DownloadIcon />
              </div>
              <div
                onClick={() => downloadTemplate(document.id || document.subDocuments?.[0]?.id)}
                className="ml-1"
              >
                {t("screens.documents.downloadTemplate")}
              </div>
            </div>
          )}
        </div>

        {document.statusCode && (
          <div className="text-[12px] mb-2 leading-[16px] bg-white p-4 rounded-[4px]">
            {!document.subDocuments || (document.subDocuments && document.subDocuments?.length === 0) ?
              <>
                <div className="mb-[6px]">
                  {t("screens.documents.uploaded")}{" "}
                  <span>
                    {!!document.uploadedOn
                      ? dayjs(document.uploadedOn).format("DD/MM/YYYY")
                      : "--"}
                  </span>
                </div>
                <div>
                  <strong>
                    {t("screens.documents.validUntil")}
                    {!!document.validUntil
                      ? dayjs(document.validUntil).format("DD/MM/YYYY")
                      : "--"}
                  </strong>
                </div>
              </> : <> </>}

            <div className={`flex items-center justify-between text-sm leading-[17px] 
              ${!document.subDocuments || (document.subDocuments && document.subDocuments?.length === 0) && 'pt-4'}`
            }>
              <div className="flex items-center min-w-0 text-center">
                <div className={`flex items-center w-max px-1 rounded-[30px] text-white ${document.statusCode === 605530007
                  ? 'bg-[#EABD11]' // "To be uploaded"
                  : document.statusCode === 1 || document.statusCode === 605530002
                    ? 'bg-green' //"Active" or "Validated"
                    : document.statusCode === 605530005 || document.statusCode === 605530006
                      ? 'bg-[#CF0A2C]' // "Expired" or "Not Acceptable"
                      : document.statusCode === 605530001
                        ? 'bg-[#3987E2]' // "To be evaluated"
                        : 'bg-gray-500' // Default background color
                  }`}>
                  <div>{getDocumentsStatusIcon(document.statusCode, 'white')}</div>
                  <div className="font-[500] text-[12px] uppercase leading-[14px] px-[4px] py-[3px]">{document.status}</div>
                </div>
              </div>

              {!!document.notes && (
                <div className="flex items-center flex-shrink-0">
                  <div className="w-px h-6 bg-[#6F6F6F] mx-4"></div>
                  <div className="relative">
                    <div
                      className="cursor-pointer"
                      id={`tooltip-documents-note-trigger-${document.id || document.subDocuments?.[0]?.id}`}
                    >

                      <NotesIcon className=" w-[24px] h-[24px]" />
                    </div>

                    <Tooltip
                      id={`tooltip-documents-note-${document.id || document.subDocuments?.[0]?.id}`}
                      triggerId={`tooltip-documents-note-trigger-${document.id || document.subDocuments?.[0]?.id}`}
                      title={t("screens.documents.note")}
                      description={document.notes}
                    />
                  </div>

                </div>
              )}
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default DocumentCard;
